import React from 'react';
import { Box, Typography, Container, Card, CardContent, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import Header from '../components/Header';
import MartiniBanner from '../components/MartiniBanner';
import { STRIPE_PAYMENT_LINK } from '../config/stripe';

const About: React.FC = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const colors = {
    bg: isDark ? '#0f0f0f' : '#f5f5f5',
    cardBg: isDark ? '#1a1a1a' : '#ffffff',
    text: isDark ? '#e0e0e0' : '#1a1a1a',
    textSecondary: isDark ? '#999' : '#666',
    textMuted: isDark ? '#555' : '#aaa',
    border: isDark ? '#2a2a2a' : '#e0e0e0',
    hover: isDark ? '#252525' : '#f8f8f8',
    accent: isDark ? '#C084FC' : '#9333EA',
  };

  return (
    <>
      <SEO 
        title="About - Daily Stories"
        description="Learn about Daily Stories, our mission to create sophisticated erotic fiction, and how you can contribute your own stories."
        keywords="about daily stories, erotic fiction, submit stories, adult fiction"
        url="https://dailystories.fr/about"
      />
      
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: colors.bg
      }}>
        {/* Header */}
        <Header isMobile={isMobile} />

        {/* Content */}
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 }, px: { xs: 3, md: 4 } }}>
          {/* Hero Section */}
          <Box sx={{ mb: 8, textAlign: 'center' }}>
            <Box
              component="img"
              src="/podcast.png"
              alt="Daily Stories"
              sx={{
                width: { xs: '80px', md: '100px' },
                height: 'auto',
                mx: 'auto',
                mb: 4,
                opacity: 0.95,
              }}
            />
            <Typography
              variant="h1"
              sx={{ color: colors.text, mb: 3, fontSize: { xs: '2.5rem', md: '3.5rem' } }}
            >
              About Daily Stories
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: colors.textSecondary, maxWidth: '700px', mx: 'auto', fontSize: { xs: '1.1rem', md: '1.25rem' } }}
            >
              Intimate fiction for the discerning reader
            </Typography>
          </Box>

          {/* Our Story */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h2"
              sx={{ color: colors.text, mb: 3 }}
            >
              Hi, I'm Marin 👋
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: colors.textSecondary, mb: 3 }}
            >
              I started Daily Stories because I believe erotic fiction deserves the same literary care as any other genre. I write intimate tales set in Europe's most romantic cities, celebrating desire in all its beautiful forms.
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: colors.textSecondary, mb: 3 }}
            >
              Every story is crafted to evoke emotion, atmosphere, and connection. I'm passionate about LGBTQ+ representation, sex-positivity, and creating a safe space where you can explore sensuality through literature.
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: colors.textSecondary }}
            >
              Whether you're reading on your commute, listening to the podcast, or winding down for the evening, I hope these stories add a touch of romance and excitement to your day.
            </Typography>
          </Box>

          {/* Submit Your Story */}
          <Card
            sx={{
              backgroundColor: isDark ? 'rgba(147, 51, 234, 0.05)' : 'rgba(147, 51, 234, 0.03)',
              border: `1px solid ${isDark ? 'rgba(147, 51, 234, 0.2)' : 'rgba(147, 51, 234, 0.15)'}`,
              mb: 6
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Typography
                variant="h2"
                sx={{ color: colors.text, mb: 3 }}
              >
                Submit Your Story
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: colors.textSecondary, mb: 3 }}
              >
                I love reading submissions from talented writers who share my vision for sophisticated erotic fiction. If you have a story that fits the vibe, I'd genuinely love to read it.
              </Typography>
              
              <Typography
                variant="h3"
                sx={{
                  fontSize: '1.25rem',
                  fontWeight: 500,
                  color: colors.text,
                  mb: 2,
                  mt: 3
                }}
              >
                Submission Guidelines
              </Typography>
              <Box component="ul" sx={{ color: colors.textSecondary, fontSize: '1.05rem', lineHeight: 1.8, pl: 3 }}>
                <li>Stories should be 1,500-3,000 words</li>
                <li>Set in a European city (or another romantic location)</li>
                <li>Well-written with attention to atmosphere and character</li>
                <li>Respectful and consensual content only</li>
                <li>LGBTQ+ stories are especially welcome</li>
                <li>Original work that you own the rights to</li>
              </Box>

              <Typography
                variant="h3"
                sx={{
                  fontSize: '1.25rem',
                  fontWeight: 500,
                  color: colors.text,
                  mb: 2,
                  mt: 3
                }}
              >
                How to Submit
              </Typography>
              <Typography
                sx={{
                  fontSize: '1.05rem',
                  color: colors.textSecondary,
                  lineHeight: 1.8,
                  mb: 2
                }}
              >
                Here's how to submit:
              </Typography>
              <Box component="ol" sx={{ color: colors.textSecondary, fontSize: '1.05rem', lineHeight: 1.8, pl: 3, mb: 3 }}>
                <li>
                  <a 
                    href={STRIPE_PAYMENT_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: colors.accent, textDecoration: 'none' }}
                  >
                    Buy me a martini
                  </a> to support this project (€5)
                </li>
                <li>Save your receipt/confirmation</li>
                <li>Email your story to <strong style={{ color: colors.text }}>cem@plotcamp.com</strong></li>
                <li>Include your receipt and a brief author bio</li>
              </Box>
              <Typography
                sx={{
                  fontSize: '0.95rem',
                  color: colors.textMuted,
                  fontStyle: 'italic',
                  lineHeight: 1.6
                }}
              >
                I read every submission personally and respond within 2-3 days. If I love your story, we'll work together on any edits and I'll feature you as a guest author. The martini helps cover hosting costs and shows you're serious about your craft.
              </Typography>
            </CardContent>
          </Card>

          {/* Blog Section */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2rem' },
                fontWeight: 400,
                color: colors.text,
                mb: 3,
                fontFamily: 'Georgia, "Times New Roman", serif'
              }}
            >
              Blog & Resources
            </Typography>
            <Card
              onClick={() => navigate('/articles')}
              sx={{
                backgroundColor: colors.cardBg,
                border: `1px solid ${colors.border}`,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: colors.hover,
                  transform: 'translateX(4px)'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  sx={{
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    color: colors.text,
                    mb: 1
                  }}
                >
                  📝 Blog & Articles
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    color: colors.textSecondary
                  }}
                >
                  Guides, reflections, and insights about erotic fiction
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Support & Legal */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2rem' },
                fontWeight: 400,
                color: colors.text,
                mb: 3,
                fontFamily: 'Georgia, "Times New Roman", serif'
              }}
            >
              Support & Legal
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Card
                onClick={() => navigate('/privacy-policy')}
                sx={{
                  backgroundColor: colors.cardBg,
                  border: `1px solid ${colors.border}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: colors.hover,
                    transform: 'translateX(4px)'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    sx={{
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      color: colors.text,
                      mb: 1
                    }}
                  >
                    Privacy Policy
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: colors.textSecondary
                    }}
                  >
                    How I handle your data and protect your privacy
                  </Typography>
                </CardContent>
              </Card>

              <Card
                onClick={() => navigate('/terms-of-service')}
                sx={{
                  backgroundColor: colors.cardBg,
                  border: `1px solid ${colors.border}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: colors.hover,
                    transform: 'translateX(4px)'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    sx={{
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      color: colors.text,
                      mb: 1
                    }}
                  >
                    Terms of Service
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: colors.textSecondary
                    }}
                  >
                    Terms and conditions for using Daily Stories
                  </Typography>
                </CardContent>
              </Card>

              <Card
                onClick={() => navigate('/support')}
                sx={{
                  backgroundColor: colors.cardBg,
                  border: `1px solid ${colors.border}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: colors.hover,
                    transform: 'translateX(4px)'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    sx={{
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      color: colors.text,
                      mb: 1
                    }}
                  >
                    Support & Contact
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: colors.textSecondary
                    }}
                  >
                    Questions, feedback, or technical issues? Get in touch
                  </Typography>
                </CardContent>
              </Card>

              <Card
                component="a"
                href={STRIPE_PAYMENT_LINK}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  backgroundColor: colors.cardBg,
                  border: `1px solid ${colors.border}`,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  display: 'block',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: colors.hover,
                    transform: 'translateX(4px)'
                  }
                }}
              >
              </Card>
            </Box>
          </Box>

          {/* Martini Banner */}
          <MartiniBanner />

          {/* iOS App */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2rem' },
                fontWeight: 400,
                color: colors.text,
                mb: 3,
                fontFamily: 'Georgia, "Times New Roman", serif'
              }}
            >
              Download the App
            </Typography>
            <Card
              component="a"
              href="https://apps.apple.com/app/daily-stories-18/id6738640121"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                backgroundColor: isDark ? 'rgba(147, 51, 234, 0.05)' : 'rgba(147, 51, 234, 0.03)',
                border: `1px solid ${isDark ? 'rgba(147, 51, 234, 0.2)' : 'rgba(147, 51, 234, 0.15)'}`,
                cursor: 'pointer',
                textDecoration: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(147, 51, 234, 0.08)' : 'rgba(147, 51, 234, 0.05)',
                  transform: 'translateY(-2px)',
                  boxShadow: isDark 
                    ? '0 8px 24px rgba(147, 51, 234, 0.15)'
                    : '0 8px 24px rgba(147, 51, 234, 0.1)',
                }
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '3rem', mb: 2 }}>
                  📱
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    color: colors.text,
                    mb: 1
                  }}
                >
                  Daily Stories 18+ for iOS
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1.05rem',
                    color: colors.textSecondary,
                    lineHeight: 1.6,
                    mb: 2
                  }}
                >
                  Get the full experience on your iPhone or iPad. Read stories offline, listen to audio narrations, and never miss a new tale.
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.9rem',
                    color: colors.accent,
                    fontWeight: 500,
                    letterSpacing: '0.5px'
                  }}
                >
                  Download on the App Store →
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Footer Note */}
          <Box sx={{ textAlign: 'center', pt: 4, borderTop: `1px solid ${colors.border}` }}>
            <Typography
              sx={{
                fontSize: '0.9rem',
                color: colors.textMuted,
                lineHeight: 1.8
              }}
            >
              Daily Stories is a solo project made with love.<br />
              All stories are works of fiction. All characters are entirely fictional.<br />
              For readers 18+ only. Discretion advised.
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default About;
